// ═════════════════════════════════════════════════════════════════════════════
//  LO QUE PUEDES PEDIRLE AL COACH
//
//  Mismo diseno que los botones de ayuda de Spacy en lecciones: acordeon con
//  cabecera, rejilla de botones con emoji, etiqueta, subetiqueta y su color.
//
//  ── POR QUE COMPARTE EL CSS Y NO EL COMPONENTE ──────────────────────────────
//  Se reutiliza `spacy-panic.css` tal cual —esta acotado al prefijo `spb-`, sin
//  un solo selector suelto— asi que los dos sitios se ven IGUAL y seguiran
//  viendose igual: si alguien retoca ese CSS, cambian los dos. Eso es la
//  sincronia que pidio Rob.
//
//  Lo que no se reutiliza es el componente. `SpacyPanicButtons` lleva sus
//  botones fijos dentro, elegidos por `mode`, y arrastra estados que aqui no
//  existen: pausa de escena, carga por tipo de ayuda, "escribe continuar". Su
//  propio fichero empieza con un "TODO: volver aqui para arreglar el boton de
//  panico". Meterle un modo mas a un componente que ya tiene un fallo abierto
//  es la forma de heredar ese fallo en una pantalla que hoy funciona.
//
//  Aqui las acciones son otra cosa: cada boton manda un mensaje al coach y ya.
// ═════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { fxTap } from "../audio/fx";
import "../exercises/spacy-panic.css";

// `textos` llega de fuera y no se importa aqui: este componente no tiene por
// que saber que idiomas existen ni como se decide cual toca. Recibe lo que
// tiene que pintar y lo pinta, que es lo que le hace reutilizable.
export default function CoachAcciones({ acciones, textos, disabled = false, onAccion, senalDePlegado = 0 }) {
  // ── ARRANCA ABIERTO SOLO SI HAY SITIO ──────────────────────────────────────
  // Los cuatro botones en dos filas ocupan unos 190px. Medido en el banco de
  // pantallas: en un iPhone SE (568px de alto, menos 122 de cabecera y barra
  // inferior) abiertos de salida dejaban la conversacion en 16px. El menu se
  // comia la conversacion, que es justo lo que el alumno viene a leer.
  //
  // Asi que se abren solos cuando la pantalla da para ello, y en las cortas
  // esperan detras de su cabecera. Para eso existe un acordeon; el alumno los
  // abre cuando los quiere y ahi si vale la pena que ocupen.
  //
  // El corte esta en 800px y no en un numero redondo: es lo que hace falta para
  // que quepan el personaje, los cuatro botones, la caja de escribir y todavia
  // una conversacion que se lea. Debajo de eso algo se queda sin sitio, y no
  // puede ser la conversacion.
  const [abierto, setAbierto] = useState(() => {
    try { return window.matchMedia("(min-height: 800px)").matches; }
    catch { return true; }
  });

  // ── CUANDO EL COACH PIDE SITIO ──────────────────────────────────────────
  //  Rob: «si doy clic se baja la barra de entrenador para ver lo que manda el
  //  coach». El recorrido cuenta sus once funciones DENTRO del hilo, y con esta
  //  botonera desplegada debajo la conversacion se queda sin sitio justo cuando
  //  es lo unico que hay que leer.
  //
  //  Llega como una senal —un numero que sube— y no como un booleano: asi el
  //  de fuera puede decir «pliegate ahora» sin quedarse mandando para siempre,
  //  y el alumno la vuelve a abrir en cuanto quiera.
  useEffect(() => {
    if (senalDePlegado > 0) setAbierto(false);
  }, [senalDePlegado]);

  return (
    <div
      className={[
        "spb-container",
        abierto ? "spb-accordion-open" : "spb-accordion-closed",
      ].join(" ")}
    >
      <button
        className="spb-header spb-header-toggle"
        onClick={() => { fxTap(); setAbierto(v => !v); }}
        aria-expanded={abierto}
        aria-label={abierto ? textos.ocultarAcciones : textos.mostrarAcciones}
      >
        <span className="spb-header-icon">🏋️</span>
        <span className="spb-header-text">{textos.entrenar}</span>
        <span className="spb-header-hint">
          {disabled
            ? textos.spacyPensando
            : abierto
              ? textos.tocaParaOcultar
              : textos.queQuieresHacer}
        </span>

        {!abierto && <span className="spb-pill">{acciones.length}</span>}

        <span className={`spb-chevron ${abierto ? "spb-chevron-open" : ""}`}>›</span>
      </button>

      <div className="spb-grid-wrapper" aria-hidden={!abierto}>
        <div className="spb-grid">
          {acciones.map(a => (
            <div className="spb-btn-wrapper" key={a.id}>
              <button
                className={`spb-btn ${disabled ? "spb-disabled" : ""}`
                  + (a.destacado ? " cch-destacado" : "")
                  /* Apagado no es deshabilitado: se pulsa igual. Es "esto ya
                     lo tienes", y por eso baja el tono en vez de bloquearse. */
                  + (a.apagada ? " cch-apagada" : "")}
                style={{ "--btn-color": a.color }}
                /* ── PULSAR CIERRA LA BARRA ───────────────────────────────
                   Rob, 29-09-2026: «quiero que cuando le den clic a un botón
                   la barra se esconda, para que cuando tengan la respuesta no
                   moleste; y si quieren apretar otro, que abran las opciones,
                   den clic y se cierre para ver la respuesta».

                   Se cierra ANTES de llamar a la acción, no después: así la
                   barra ya se ha apartado mientras llega la respuesta, que es
                   el momento en el que estorbaba. Cerrarla al terminar la
                   dejaría tapando justo lo que el alumno está esperando.

                   Hasta hoy esto sólo pasaba con «Cómo funciona», porque era
                   el único que llamaba a `apartaLaBotonera()` desde la
                   pantalla. Ponerlo aquí hace que valga para todos los botones
                   y para quien añada uno mañana. */
                onClick={() => {
                  if (disabled) return;
                  fxTap();
                  setAbierto(false);
                  onAccion(a);
                }}
                disabled={disabled}
                aria-label={a.mensaje}
              >
                <span className="spb-emoji">{a.emoji}</span>
                <span className="spb-labels">
                  <span className="spb-label">{a.etiqueta}</span>
                  <span className="spb-sublabel">{a.sub}</span>
                </span>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
